import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import LanguageDropdown from './LanguageDropdown';
import MenuOverlay from './MenuOverlay';

const SCROLL_THRESHOLD = 200;

// Solid navbar detection: true once scrolled past threshold
const useScrolled = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return isScrolled;
};

const getNavLinks = (t) => [
  { to: '/lodge-room', label: t('nav.lodgeRoom') },
  { to: '/villa', label: t('nav.villa') },
  { to: '/explore', label: t('nav.explorations') },
  { to: '/dining', label: t('nav.dining') },
  { to: '/community', label: t('nav.community') },
  { to: '/gallery', label: t('nav.gallery') },
  { to: '/getting-here', label: t('nav.gettingHere') },
  { to: '/contact', label: t('nav.contact') },
];

const NavbarLogo = ({ solid, isHome }) => {
  const handleClick = (e) => {
    if (isHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: solid ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      className="absolute left-1/2 -translate-x-1/2"
    >
      <Link 
        to="/"
        onClick={handleClick}
        className={`text-xl md:text-2xl tracking-[0.2em] md:tracking-[0.25em] transition-colors duration-300 ${
          solid ? 'text-black/80' : 'text-white/90'
        }`}
        style={{ 
          fontFamily: '"IvyPresto Text", serif', 
          fontWeight: 300,
          WebkitFontSmoothing: 'antialiased'
        }}
        data-testid="navbar-logo"
      >
        RESPIRO
      </Link>
    </motion.div>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHome = pathname === '/';
  const isScrolled = useScrolled();

  // On subpages the navbar is always solid (white bg, black text)
  const solid = isScrolled || !isHome;
  const navLinks = getNavLinks(t);

  const handleReserveClick = () => {
    setIsMenuOpen(false);
    navigate('/contact');
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          solid 
            ? 'bg-white/95 backdrop-blur-md shadow-sm' 
            : 'bg-transparent'
        }`}
        data-testid="navbar"
      >
        <div className="container mx-auto px-4 md:px-12 max-w-[1400px]">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Left Side - Menu Button + Language (mobile) */}
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`flex items-center uppercase tracking-[0.15em] text-xs font-medium transition-colors duration-300 ${
                  solid ? 'text-black' : 'text-white'
                }`}
                data-testid="menu-toggle"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={26} className="md:w-6 md:h-6" /> : <Menu size={26} className="md:w-6 md:h-6" />}
              </button>
              
              <LanguageDropdown solid={solid} mobile />
            </div>

            <NavbarLogo solid={solid} isHome={isHome} />

            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              <LanguageDropdown solid={solid} />

              {/* Reserve Button */}
              <button
                onClick={handleReserveClick}
                className={`uppercase tracking-[0.1em] md:tracking-[0.15em] text-xs font-medium px-4 py-2.5 md:px-6 md:py-3 transition-all duration-300 ${
                  solid 
                    ? 'bg-[#D4C5B0] text-black hover:bg-[#c4b5a0]' 
                    : 'bg-transparent border border-white text-white hover:bg-white/10'
                }`}
                data-testid="nav-book-btn"
              >
                {t('nav.book')}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      <MenuOverlay 
        isOpen={isMenuOpen} 
        onClose={() => setIsMenuOpen(false)} 
        navLinks={navLinks} 
      />
    </>
  );
};

export default Navbar;
